/**
 * Samples an image via canvas (downscaled to maxDim px) and returns the
 * average perceived brightness (0–255) of all non-transparent pixels,
 * using the standard luma coefficients (ITU-R BT.601).
 *
 * Returns 128 when the image has no opaque pixels or cannot be read.
 */
export function getImageAverageBrightness(
  img: HTMLImageElement,
  maxDim = 32,
): number {
  if (!img.naturalWidth || !img.naturalHeight) return 128;

  const scale = Math.min(
    maxDim / img.naturalWidth,
    maxDim / img.naturalHeight,
    1,
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));

  const ctx = canvas.getContext('2d');
  if (!ctx) return 128;

  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  let totalBrightness = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] > 0) {
      totalBrightness +=
        0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      count++;
    }
  }

  return count > 0 ? totalBrightness / count : 128;
}
